import React, { useState } from 'react';
import { Popover, DatePicker, Button, Text } from '@shopify/polaris';
import { EditMinor } from '@shopify/polaris-icons';

// Expiry dates travel as calendar dates (YYYY-MM-DD). They are built and read in
// local time here so the date picked is the date shown, whatever the timezone.
export function toDateValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function fromDateValue(value) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function formatDateValue(value) {
  if (!value) return 'Never';
  return fromDateValue(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// An override's expiry in the list: the date (or "Never") with a small edit icon
// that opens a calendar. Past dates can't be picked; "Never expires" clears it.
function ExpiryPicker({ value, onChange }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Open on the chosen date's month, or this month when there's none or it has
  // passed (a past month would show nothing that can be picked).
  const monthToShow = () => {
    const chosen = value ? fromDateValue(value) : null;
    const shown = chosen && chosen >= today ? chosen : today;
    return { month: shown.getMonth(), year: shown.getFullYear() };
  };

  const [open, setOpen] = useState(false);
  const [{ month, year }, setMonth] = useState(monthToShow);

  const openPicker = () => {
    setMonth(monthToShow());
    setOpen(true);
  };

  const activator = (
    <Button plain icon={EditMinor} onClick={openPicker} accessibilityLabel="Change expiry date" />
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
      <Text as="span">{formatDateValue(value)}</Text>
      <Popover active={open} activator={activator} onClose={() => setOpen(false)} preferredAlignment="right">
        <div style={{ padding: '16px', width: '276px' }}>
          <DatePicker
            month={month}
            year={year}
            onMonthChange={(nextMonth, nextYear) => setMonth({ month: nextMonth, year: nextYear })}
            selected={value ? fromDateValue(value) : undefined}
            disableDatesBefore={today}
            onChange={({ start }) => {
              onChange(toDateValue(start));
              setOpen(false);
            }}
          />
          <div style={{ marginTop: '12px', textAlign: 'right' }}>
            <Button
              size="slim"
              disabled={!value}
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Never expires
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
}

export default ExpiryPicker;
